import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

interface NavbarProps {
  transparent?: boolean;
}

export default function Navbar({ transparent = false }: NavbarProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { to: '/', label: '首页', icon: 'ri-home-4-line' },
    { to: '/category/movie', label: '电影', icon: 'ri-movie-2-line' },
    { to: '/category/tv', label: '电视剧', icon: 'ri-tv-line' },
    { to: '/category/variety', label: '综艺', icon: 'ri-live-line' },
    { to: '/category/anime', label: '动漫', icon: 'ri-ghost-smile-line' },
    { to: '/about', label: '关于我们', icon: 'ri-information-line' },
  ];

  const bgClass = isHome && transparent && !isScrolled
    ? 'bg-transparent'
    : 'bg-white/95 backdrop-blur-md';

  const textClass = isHome && transparent && !isScrolled
    ? 'text-white'
    : 'text-foreground-800';

  return (
    <>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgClass} ${isScrolled ? 'shadow-sm' : ''}`}>
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className={`flex items-center gap-2 ${textClass} whitespace-nowrap`}>
              <div className="w-9 h-9 flex items-center justify-center bg-primary-500 rounded-lg">
                <i className="ri-play-circle-fill text-white text-xl"></i>
              </div>
              <span className="text-xl font-bold font-heading">91视频</span>
            </Link>

            <div className="hidden md:flex items-center gap-1">
              {navLinks.map((link) => {
                const isActive = link.to === '/'
                  ? location.pathname === '/'
                  : location.pathname.startsWith(link.to);
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all duration-200 ${
                      isActive
                        ? 'bg-primary-500/10 text-primary-600'
                        : `${textClass} hover:bg-foreground-50/10`
                    }`}
                  >
                    <span className="w-4 h-4 inline-flex items-center justify-center mr-1.5">
                      <i className={`${link.icon}`}></i>
                    </span>
                    {link.label}
                  </Link>
                );
              })}
            </div>

            <div className="hidden md:flex items-center gap-3">
              <div className={`relative ${textClass}`}>
                <input
                  type="text"
                  placeholder="搜索视频..."
                  className="pl-9 pr-4 py-2 rounded-full bg-foreground-50/10 border border-foreground-200/30 text-sm w-48 focus:w-64 
                  focus:outline-none focus:border-primary-400 transition-all duration-300 placeholder:text-foreground-400"
                />
                <span className="w-4 h-4 flex items-center justify-center absolute left-3 top-1/2 -translate-y-1/2">
                  <i className="ri-search-line text-foreground-400"></i>
                </span>
              </div>
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg ${textClass}`}
              aria-label="菜单"
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)}></div>
          <div className="absolute top-16 left-0 right-0 bg-white shadow-lg rounded-b-2xl p-4">
            {navLinks.map((link) => {
              const isActive = link.to === '/'
                ? location.pathname === '/'
                : location.pathname.startsWith(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                    isActive
                      ? 'bg-primary-500/10 text-primary-600'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`}
                >
                  <span className="w-5 h-5 flex items-center justify-center">
                    <i className={link.icon}></i>
                  </span>
                  {link.label}
                </Link>
              );
            })}
            <div className="mt-2 px-4">
              <input
                type="text"
                placeholder="搜索视频..."
                className="w-full pl-9 pr-4 py-2.5 rounded-full bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-400 transition-all"
              />
              <span className="w-4 h-4 flex items-center justify-center absolute mt-[-34px] ml-3">
                <i className="ri-search-line text-foreground-400 text-sm"></i>
              </span>
            </div>
          </div>
        </div>
      )}
    </>
  );
}